<template>
  <article v-if="page" class="page">
    <div class="page__nav">
      <BackendLink :path />
      <div v-if="translations.length > 1" class="page__language-switcher">
        <select v-model="langcode">
          <option v-for="language in translations" :key="language.code" :value="language.code">
            {{ language.name }}
          </option>
        </select>
      </div>
    </div>
    <Blocks v-if="page.content?.list" :items="page.content?.list" class="page__content" />
  </article>
  <h1 v-else>
    404
  </h1>
</template>

<script setup lang="ts">
import { Langcode, type PageFullFragment, type PageQuery, type PageQueryVariables } from '#graphql-operations'

const appConfig = useAppConfig()
const appRoute = useRoute()

const translations = ref([])
const langcode = ref('de')

if (typeof appRoute.query.language === 'string') {
  langcode.value = appRoute.query.language
}

const props = defineProps<{
  path: string
}>()

const variables = computed<PageQueryVariables>(() => {
  const langcodeKey = langcode.value.toUpperCase() as keyof typeof Langcode
  return {
    path: props.path,
    langcode: Langcode[langcodeKey]
  }
})

const { data } = await useAsyncGraphqlQuery('page', variables, {
  transform: function (v) {
    return v.data as PageQuery
  }
})

const route = computed<EntityRoute | undefined | null>(() => {
  return data.value?.route
})

await useHandleRedirect(route)

if (route.value?.entity === undefined) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

await useHandleRedirect(route)

const page = computed((): PageFullFragment => {
  return route.value?.entity?.translation
})

function getLanguages (translations: Array) {
  const languages = []
  translations.forEach((element) => {
    const language = appConfig.languages.find((language) => {
      return language.code === element.langcode
    })
    if (language !== undefined) {
      languages.push(language)
    }
  })
  return languages
}

if (page?.value?.translations && page?.value?.translations.length > 1) {
  translations.value = getLanguages(page.value.translations)
}

useHead({
  title: page.title
})
</script>

<style lang="scss" scoped>
@use "~/assets/sass/tools";

.page {
  position: relative;

  &__nav {
    display: flex;
    justify-content: space-between;
    max-width: 200px;
    @include tools.to(tools.$breakpoint--mobile) {
      margin-bottom: var(--space--m);
    }
    @include tools.from(tools.$breakpoint--desktop) {
      position: absolute;
      right: 0;
      width: 100%;
      max-width: 200px;
    }
  }

  &__language-switcher {
    max-width: 200px;
    @include tools.to(tools.$breakpoint--mobile) {
      margin-bottom: var(--space--m);
    }

    select {
      @include tools.form-field-select;
    }
  }

  &__content {
    @include tools.grid;
  }
}
</style>
